'use client';

import { defineStyleConfig, Flex, Grid, useStyleConfig, GridProps } from '@chakra-ui/react';

import Text from '@/components/atomic/v1/Text';
import Tooltip, { TooltipType } from '@/components/atomic/v1/tooltip/Tooltip';
import Icon, { IconProps } from '@/components/atomic/v1/icon/Icon';
import Tag, { TagProps } from '@/components/atomic/v1/tag/Tag';

export const dataSetTheme = defineStyleConfig({
  variants: {
    none: {
      backgroundColor: 'transparent'
    },
    white: {
      backgroundColor: 'background/white'
    },
    primary: {
      backgroundColor: 'background/primary'
    },
    secondary: {
      backgroundColor: 'background/secondary'
    }
  }
});

interface DataSetProps extends GridProps {
  variant: 'none' | 'white' | 'primary' | 'secondary';
  direction: 'horizontal' | 'vertical';
  label: string | React.ReactNode;
  children: React.ReactNode;
  tooltipProps?: Omit<TooltipType, 'tooltipText' | 'icon'> | undefined;
  iconProps?: IconProps;
  tagProps?: TagProps;
  hasBorder?: boolean;
}

const DataSet = ({
  variant,
  direction,
  iconProps,
  label,
  children,
  tooltipProps,
  tagProps,
  hasBorder,
  ...rest
}: DataSetProps) => {
  const styles = useStyleConfig('DataSet', { variant });

  return (
    <Grid
      columnGap={direction === 'horizontal' ? 4 : 2}
      rowGap={direction === 'horizontal' ? 0 : 4}
      sx={styles}
      borderStyle={hasBorder ? 'solid' : 'none'}
      borderWidth={hasBorder ? '2px' : 'none'}
      borderColor={hasBorder ? 'grey.40' : 'transparent'}
      borderRadius="8px"
      padding={variant === 'none' ? 0 : { base: 4, md: 6 }}
      gridAutoColumns={direction === 'horizontal' ? 'min-content auto' : 'auto'}
      gridAutoFlow={direction === 'horizontal' ? 'column' : 'row'}
      {...rest}
    >
      {iconProps && (
        <Icon
          gridRow={direction === 'vertical' ? '1 / 2' : '1 / 3'}
          flexShrink={0}
          color="icon/default"
          {...iconProps}
        />
      )}

      {tagProps && (
        <Tag
          gridRow="1 / 2"
          marginLeft={direction === 'horizontal' ? 'initial' : 'auto'}
          marginBottom={direction === 'horizontal' ? 3 : 0}
          height="fit-content"
          width="fit-content"
          {...tagProps}
        />
      )}

      <Flex flexDirection="column" gap={3} flexWrap="wrap" gridColumn={direction === 'vertical' ? '1 / 3' : 'auto'}>
        <Flex columnGap={2}>
          <Text fontWeight="bold">{label}</Text>
          {tooltipProps && <Tooltip tooltipText="" icon="question" {...tooltipProps} />}
        </Flex>

        {children}
      </Flex>
    </Grid>
  );
};

export default DataSet;
